import React, { useContext, useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Icon, Label, Loader, Menu, SemanticICONS, Transition } from 'semantic-ui-react';
import { StyledMenu, StyledMenuMooh } from './style';
import {
  getSideMenuDairyItems,
  getSideMenuFarmerItems,
  getSideMenuFarmerManagerItems,
  getSideMenuIfeItems
} from './constants'
import { useAuth } from '~/routes/components/AuthProvider';
import {
  ADMIN_ROLE_SLUG,
  CLIMATE_CHECK_SLUG,
  CLOUD_STORAGE_SLUG,
  CO2_LFL_SLUG,
  FARMER_MANAGER_ROLE_SLUG,
  FARMER_ROLE_SLUG,
  FAT_PROTEIN_SLUG,
  FUTURE_CONTRACTS_SLUG,
  IFE_ROLE_SLUG,
  LIGHT_USER_ROLE_SLUG,
  MILK_FIXED_SLUG,
  MILK_STOCK_EXCHANGE,
  OPERATIVE_ROLE_SLUG
}
  from '~/Constants';
import { useTranslation } from 'react-i18next';
import notify from '../Notify';
import cloneDeep from 'lodash.clonedeep'
import MilkFixedService from '~/api/services/milkFixedPrice';
import NewsletterService from '~/api/services/newsletter';
import SurveyService from '~/api/services/survey';
import FatAndProteinService from '~/api/services/fatAndProtein';
import StockExChangeService from '~/api/services/stockExchange';
import CO2Service from '~/api/services/co2';
import { IndicatorsContext } from '../InnerContent';
import ClimaCheckService from '~/api/services/climaCheck';

interface IMenuAccordionTree {
  id: string;
  title: string;
  iconClassName?: string;
  children?: IMenuAccordionTree[];
  link?: string;
  fixed?: boolean;
  displayForRole?: string[];
  displayInTestEnv?: boolean;
  changeLinkRole?: string[],
  changeLinkUrl?: string
  slug?: string | string[]
  indicator?: number | string
}
const SideMenu: React.FunctionComponent = () => {
  const { t } = useTranslation()
  const {
    systemData,
    systemData: {
      operativeUsersCanSeeFarmersInfo,
      stonexEnabled,
      modules,
      chEnv,
      faqSettings,
      cloudStorageSettings: { farmer_enabled }
    }
  } = useAuth()

  const [
    sideMenuDairyItems,
    sideMenuFarmerItems,
    sideMenuFarmerManagerItems,
    sideMenuIfeItems
  ] = [getSideMenuDairyItems(Boolean(chEnv)), getSideMenuFarmerItems(), getSideMenuFarmerManagerItems(), getSideMenuIfeItems()]

  const location = useLocation();
  const [openMenus, setOpenMenus] = useState(['dairy'])
  const hasMilkFixedModule = modules.find(module => module.slug === MILK_FIXED_SLUG)
  const hasFatProteinModule = modules.find(module => module.slug === FAT_PROTEIN_SLUG)
  const hasStockExchangeModule = modules.find(module => module.slug === MILK_STOCK_EXCHANGE)
  const farmerLogged = systemData?.logged?.roleSlug === FARMER_ROLE_SLUG
  const cloudStorageText = systemData?.texts.find(text => text.slug === CLOUD_STORAGE_SLUG)
  const hasCo2Lfl = modules.find(module => module.slug === CO2_LFL_SLUG)
  const hasClimateCheck = modules.find(module => module.slug === CLIMATE_CHECK_SLUG)
  const faqText = systemData?.texts?.find(text => text.slug === 'faq_text')
  const [isLoadingIndicators, setIsLoadingIndicators] = useState(false)
  const { indicators, setIndicators } = useContext(IndicatorsContext)

  useEffect(() => {
    const openMenus = getActiveItemIds(items, location.pathname)
    setOpenMenus(openMenus)
    if (farmerLogged) {
      fetchCounters()
    }
  }, [])

  function getActiveItemIds(items, pathname) {
    const activeIds: string[] = [];
    let startItem

    const checkItem = (item: IMenuAccordionTree, parentId?: string) => {
      if (!parentId) {
        startItem = item.id
      }
      if (item.link && pathname.startsWith(item.link)) {
        activeIds.push(parentId || item.id);
        if (!activeIds.find(id => id === startItem)) {
          activeIds.push(startItem)
        }
      }

      if (item.children) {
        item.children.forEach((child) => checkItem(child, item.id));
      }
    };
    items.forEach((item) => item.children && checkItem(item, null));
    return activeIds;
  }

  function linkIsActive(link: string, id?: string) {
    return location.pathname === '/' && id === 'dashboard' ? true : location.pathname.startsWith(link)
  }

  function handleOpenMenu(idMenu) {
    if (openMenus.find(id => id === idMenu)) {
      setOpenMenus(openMenus.filter(id => id !== idMenu))
    } else {
      setOpenMenus([...openMenus, idMenu])
    }
  }

  async function fetchCounters() {
    setIsLoadingIndicators(true)
    const itemsCopy = filterItemsBasedOnModulesEnabled(cloneDeep(items))
    try {
      const tempIndicators = [...indicators]

      if (hasCo2Lfl) {
        const response = await CO2Service.getCO2CountActive();
        if (response.data) {
          tempIndicators[0].value = response.data
        }
      }

      if (itemsCopy.find(item => item.id === 'newsletter')) {
        const response = await NewsletterService.countNewNewsletter()
        if (response.data) {
          tempIndicators[1].value = response.data
        }
      }

      if (hasMilkFixedModule) {
        const response = await MilkFixedService.countActiveTradingSlots()
        if (response.data) {
          tempIndicators[2].value = response.data
        }
      }

      if (itemsCopy.find(item => item.id === 'survey')) {
        const response = await SurveyService.countSurveys()
        if (response.data) {
          tempIndicators[3].value = response.data
        }
      }

      if (hasFatProteinModule) {
        const response = await FatAndProteinService.countActiveTradingSlots()
        if (response.data) {
          tempIndicators[4].value = response.data
        }
      }

      if (hasStockExchangeModule) {
        const response = await StockExChangeService.countActiveTradingSlots()
        if (response.data) {
          tempIndicators[5].value = response.data
        }
      }

      if (hasClimateCheck) {
        const response = await ClimaCheckService.getClimaChActive()
        if (response.data) {
          tempIndicators[6].value = response.data
        }
      }
      setIndicators(tempIndicators)
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setIsLoadingIndicators(false)
  }

  function renderIndicator(item: IMenuAccordionTree) {
    if (typeof (item.indicator) !== 'number') {
      return
    }

    if (isLoadingIndicators) {
      return <div className='ui circular label' style={{ position: 'relative', padding: 5, background: 'transparent' }}>
        <Loader size='small' active={isLoadingIndicators} inverted />
      </div>
    }

    const indicator = indicators.find(indicator => indicator.key === `${item.id}-indicator`)
    return indicator.value !== 0 && <Label
      style={{ marginLeft: 10 }}
      circular
      color='teal'
      id={`${item.id}-indicator`}
    >
      {indicator.value}
    </Label>
  }

  function renderItem(item: IMenuAccordionTree) {
    const roleSlug = systemData?.logged?.roleSlug
    const active = linkIsActive(item.link, item.id)
    const itemMenuIsOpen = Boolean(item.children && openMenus.find(id => id === item.id))
    return <Menu.Item
      as={item.children ? 'div' : Link}
      to={item.link}
      key={item.id}
      active={active}>
      <div onClick={() => item.children && handleOpenMenu(item.id)} className={`itemWrapper ${item.children && openMenus.find(id => id === item.id) ? 'dropdownDivider' : ''}`}>
        <div className={'titleWrapper'}>
          <Icon className={item.iconClassName} />
          <div style={{ marginLeft: 3 }} dangerouslySetInnerHTML={{ __html: item.title }} />
          {renderIndicator(item)}
        </div>
        {item.children && <Icon
          style={{ cursor: 'pointer' }}
          name={`chevron ${!itemMenuIsOpen ? 'right' : 'down'}` as SemanticICONS} />}
      </div>
      {item.children && <Transition visible={itemMenuIsOpen} animation='slide down' duration={300}>
        <Menu.Menu>
          {item.children.map((child) => {
            const link = child.changeLinkRole ? child.changeLinkRole.find(slug => slug === roleSlug) ? child.changeLinkUrl : child.link : child.link
            if (child.displayForRole) {
              return checkPermissions(child)
            }
            return child.children ? renderItem(child) : <Menu.Item
              active={linkIsActive(child.link, child.id)}
              as={Link}
              to={link}
              key={child.id}
              name={child.title}
            >
              <div className='titleWrapper'>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Icon className={child.iconClassName} />
                  <span style={{ marginLeft: 3 }} dangerouslySetInnerHTML={{ __html: child.title }} />
                </div>
              </div>
            </Menu.Item>
          })}
        </Menu.Menu>
      </Transition>
      }
    </Menu.Item>
  }

  function checkPermissions(item) {
    const { displayForRole } = item
    if (displayForRole.find(role => role === systemData?.logged.roleSlug)) {
      return renderItem(item)
    }

    return
  }

  function getOperativeUserWithoutFarmers() {
    const dairyItemsIndex = sideMenuDairyItems.findIndex(item => item.id === 'dairy')
    //@ts-ignore
    const filteredDairyItems = sideMenuDairyItems[dairyItemsIndex].children.filter(item => !(item.id === 'farmers' || item.id === 'farmer-users'))
    //@ts-ignore
    sideMenuDairyItems[dairyItemsIndex].children = filteredDairyItems
    const operativeSideMenuItems = sideMenuDairyItems.filter(item => item.id !== 'newsletter')
    const filteredItemsBasedOnModulesEnabled = filterItemsBasedOnModulesEnabled(operativeSideMenuItems)
    return filteredItemsBasedOnModulesEnabled
  }

  const moduleSlugs = modules.map(module => module.slug)

  function hasMatchingModule(slug: string | string[] | undefined): boolean {
    if (!slug) return true
    const slugs = Array.isArray(slug) ? slug : [slug]
    return slugs.some(slug => moduleSlugs.includes(slug))
  }

  function hasMatchingChildModule(slug: string | string[] | undefined, parentModuleSlugs): boolean {
    if (!slug) return true
    const slugs = Array.isArray(slug) ? slug : [slug]
    return slugs.some(slug => parentModuleSlugs.includes(slug))
  }

  function filterChildren(item) {
    if (item.children) {

      if (item.fixed) {
        item.children = item
          .children
          .filter(item => hasMatchingModule(item.slug))
      } else {
        const parentModuleSlugs = modules.find(module => module.slug === item.slug)?.children.map(child => child.slug)
        item.children = item.children.filter(child => hasMatchingChildModule(child.slug, parentModuleSlugs || []))
      }
    }
    return item
  }

  function filterItemsBasedOnModulesEnabled(items) {
    return items
      .filter(item => hasMatchingModule(item.slug))
      .map(item => item.slug === FUTURE_CONTRACTS_SLUG ? item : filterChildren(item))
  }

  function addCorrectLabelsFromApi() {
    const roleSlug = systemData?.logged?.roleSlug
    const displayFaq = roleSlug !== ADMIN_ROLE_SLUG ? Boolean(faqSettings.status) && faqSettings.positions.find(position => position === 'sidebar') :
      modules.find(module => module.slug === 'faq') !== undefined
    const faqItemsIndex = sideMenuDairyItems.findIndex(item => item.id === 'faq')
    if (faqItemsIndex >= 0) {
      sideMenuDairyItems[faqItemsIndex].title = faqText?.translation || t('FAQ')
    }
    const filteredItemsBasedOnModulesEnabled = filterItemsBasedOnModulesEnabled(cloneDeep(sideMenuDairyItems))
    if (!displayFaq) {
      return filteredItemsBasedOnModulesEnabled.filter((modules) => modules.id !== 'faq')
    }
    //get Dairy then remove milk payment
    if (roleSlug === LIGHT_USER_ROLE_SLUG) {
      const dairyIndex = filteredItemsBasedOnModulesEnabled.findIndex(item => item.id === 'dairy')
      const dairyChildren = filteredItemsBasedOnModulesEnabled[dairyIndex].children
      const milkMoneyIndex = filteredItemsBasedOnModulesEnabled.findIndex(item => item.id === 'milk_money_accounting')
      if (milkMoneyIndex >= 0) {
        filteredItemsBasedOnModulesEnabled[milkMoneyIndex].children = filteredItemsBasedOnModulesEnabled[milkMoneyIndex].children.filter(child => child.id !== 'ftp-logs')
      }
      if (dairyIndex >= 0) {
        filteredItemsBasedOnModulesEnabled[dairyIndex].children = dairyChildren.filter(childItem => childItem.id !== 'milk-payment')
      }
    }

    const futureContractsIndex = filteredItemsBasedOnModulesEnabled.findIndex(item => item.id === 'future_contracts')
    if (futureContractsIndex >= 0) {
      filteredItemsBasedOnModulesEnabled[futureContractsIndex].children[0].displayInTestEnv = Boolean(stonexEnabled)
    }
    return filteredItemsBasedOnModulesEnabled
  }

  function getFarmerRoleSidebarItems() {
    const displayFaq = Boolean(faqSettings.status) && faqSettings.positions.find(position => position === 'sidebar')
    let items = filterItemsBasedOnModulesEnabled(sideMenuFarmerItems)
    const faqItemsIndex = items.findIndex(item => item.id === 'faq')
    if (!displayFaq) {
      items = items.filter((_, index) => index !== faqItemsIndex)
    } else if (faqItemsIndex >= 0) {
      items[faqItemsIndex].title = faqText?.translation || t('FAQ')
    }
    const cloudStorageIndex = items.findIndex(item => item.slug === 'cloud_storage')
    if (farmer_enabled === 0) {
      return items.filter((item, index) => index !== cloudStorageIndex)
    } else if (farmer_enabled === 1 && cloudStorageIndex >= 0) {
      items[cloudStorageIndex].title = cloudStorageText.translation || cloudStorageText.label
    }
    return items
  }

  const items = getItem()

  function getItem() {
    const roleSlug = systemData?.logged?.roleSlug
    switch (roleSlug) {
      case ADMIN_ROLE_SLUG:
        return addCorrectLabelsFromApi()
      case FARMER_ROLE_SLUG:
        return getFarmerRoleSidebarItems()
      case LIGHT_USER_ROLE_SLUG:
        return addCorrectLabelsFromApi()
      case OPERATIVE_ROLE_SLUG:
        return operativeUsersCanSeeFarmersInfo === 1 ? addCorrectLabelsFromApi() : getOperativeUserWithoutFarmers()
      case FARMER_MANAGER_ROLE_SLUG:
        return filterItemsBasedOnModulesEnabled(sideMenuFarmerManagerItems)
      case IFE_ROLE_SLUG:
        return filterItemsBasedOnModulesEnabled(sideMenuIfeItems)
      default:
        return addCorrectLabelsFromApi()
    }
  }

  if (chEnv) {
    return <div style={{ padding: '10px 20px', background: '#B2B2B2', borderRadius: '1.3em' }}>
      <StyledMenuMooh vertical borderless fluid>
        {systemData && items.map((item: IMenuAccordionTree) => item.displayForRole ? checkPermissions(item) : renderItem(item))}
      </StyledMenuMooh>
    </div>
  }

  return (
    <StyledMenu vertical>
      {systemData && items.map((item: IMenuAccordionTree) => item.displayForRole ? checkPermissions(item) : renderItem(item))}
    </StyledMenu>
  )
};

export default SideMenu;
